import { database } from "../firebase/config";
import { doc, onSnapshot, setDoc, updateDoc, getDoc } from "firebase/firestore";
import { initialSchedules, processSchedule } from "../data/scheduleData";

export const scheduleService = {
  _legacyScheduleDoc(nightKey, eventKey) {
    const docId = eventKey === "sanremo" ? nightKey : `${eventKey}_${nightKey}`;
    return doc(database, "schedules", docId);
  },

  _eventScheduleDoc(eventId, subEventId) {
    return doc(database, "events", eventId, "schedules", String(subEventId));
  },

  _deliverSchedule(nightKey, data, onUpdate) {
    const isInvalidData =
      !data ||
      !data.scaletta ||
      (Array.isArray(data.scaletta) && data.scaletta.length === 0);

    if (isInvalidData) {
      const initialData = initialSchedules[nightKey];
      if (initialData) {
        const fallbackData = { ...initialData };
        if (data && data.timeOffset !== undefined) {
          fallbackData.timeOffset = data.timeOffset;
        } else {
          fallbackData.timeOffset = 0;
        }
        onUpdate(processSchedule(fallbackData));
      } else {
        onUpdate(null);
      }
      return;
    }

    try {
      const processed = processSchedule(data);
      if (data.timeOffset !== undefined) {
        processed.timeOffset = data.timeOffset;
      } else {
        processed.timeOffset = 0;
      }
      onUpdate(processed);
    } catch (err) {
      const initialData = initialSchedules[nightKey];
      if (initialData) {
        const fallbackData = { ...initialData };
        if (data && data.timeOffset !== undefined) {
          fallbackData.timeOffset = data.timeOffset;
        } else {
          fallbackData.timeOffset = 0;
        }
        onUpdate(processSchedule(fallbackData));
      } else {
        onUpdate(null);
      }
    }
  },

  // Sottoscrizione alla scaletta di una serata
  subscribeToSchedule(arg1, arg2, arg3, arg4) {
    const legacy = typeof arg1 === "string";
    const subEventId = legacy ? null : arg1;
    const nightKey = legacy ? arg1 : arg2;
    const onUpdate = legacy ? arg2 : arg3;
    const eventKey = legacy ? arg3 || "sanremo" : arg4 || "sanremo";

    if (legacy || subEventId === null || subEventId === undefined) {
      const legacyRef = this._legacyScheduleDoc(nightKey, eventKey);
      return onSnapshot(
        legacyRef,
        async (docSnap) => {
          if (docSnap.exists()) {
            this._deliverSchedule(nightKey, docSnap.data(), onUpdate);
            return;
          }

          const initialData = initialSchedules[nightKey];
          if (initialData) {
            try {
              await setDoc(legacyRef, initialData);
            } catch {
              try {
                onUpdate(processSchedule(initialData));
              } catch {
                onUpdate(null);
              }
            }
          } else {
            onUpdate(null);
          }
        },
        () => {
          const initialData = initialSchedules[nightKey];
          if (initialData) {
            try {
              onUpdate(processSchedule(initialData));
            } catch {
              onUpdate(null);
            }
          } else {
            onUpdate(null);
          }
        },
      );
    }

    const eventRef = this._eventScheduleDoc(eventKey, subEventId);
    const legacyRef = this._legacyScheduleDoc(nightKey, eventKey);
    let legacyUnsub = null;

    const unsub = onSnapshot(
      eventRef,
      async (docSnap) => {
        if (docSnap.exists()) {
          if (legacyUnsub) {
            legacyUnsub();
            legacyUnsub = null;
          }
          this._deliverSchedule(nightKey, docSnap.data(), onUpdate);
          return;
        }

        if (!legacyUnsub) {
          legacyUnsub = onSnapshot(
            legacyRef,
            async (legacySnap) => {
              if (legacySnap.exists()) {
                this._deliverSchedule(nightKey, legacySnap.data(), onUpdate);
                return;
              }
              const initialData = initialSchedules[nightKey];
              if (initialData) {
                try {
                  onUpdate(processSchedule(initialData));
                } catch {
                  onUpdate(null);
                }
              } else {
                onUpdate(null);
              }
            },
            () => {
              const initialData = initialSchedules[nightKey];
              if (initialData) {
                try {
                  onUpdate(processSchedule(initialData));
                } catch {
                  onUpdate(null);
                }
              } else {
                onUpdate(null);
              }
            },
          );
        }

        const initialData = initialSchedules[nightKey];
        if (initialData) {
          try {
            await setDoc(eventRef, initialData);
          } catch {
            try {
              onUpdate(processSchedule(initialData));
            } catch {
              onUpdate(null);
            }
          }
        } else {
          onUpdate(null);
        }
      },
      () => {
        const initialData = initialSchedules[nightKey];
        if (initialData) {
          try {
            onUpdate(processSchedule(initialData));
          } catch {
            onUpdate(null);
          }
        } else {
          onUpdate(null);
        }
      },
    );

    return () => {
      unsub();
      if (legacyUnsub) legacyUnsub();
    };
  },

  // Aggiorna l'offset temporale globale (minuti)
  async updateGlobalOffset(arg1, arg2, arg3, arg4) {
    const legacy = typeof arg1 === "string";
    const subEventId = legacy ? null : arg1;
    const nightKey = legacy ? arg1 : arg2;
    const offsetMinutes = legacy ? arg2 : arg3;
    const eventKey = legacy ? arg3 || "sanremo" : arg4 || "sanremo";
    const docRef = legacy
      ? this._legacyScheduleDoc(nightKey, eventKey)
      : this._eventScheduleDoc(eventKey, subEventId);
    try {
      await setDoc(
        docRef,
        {
          timeOffset: offsetMinutes,
        },
        { merge: true },
      );
      return true;
    } catch (error) {
      console.error("Errore nell'aggiornamento dell'offset:", error);
      throw error;
    }
  },

  // Aggiorna un singolo evento nella scaletta
  async updateEvent(arg1, arg2, arg3, arg4, arg5) {
    const legacy = typeof arg1 === "string";
    const subEventId = legacy ? null : arg1;
    const nightKey = legacy ? arg1 : arg2;
    const eventIndex = legacy ? arg2 : arg3;
    const updatedEvent = legacy ? arg3 : arg4;
    const eventKey = legacy ? arg4 || "sanremo" : arg5 || "sanremo";
    const docRef = legacy
      ? this._legacyScheduleDoc(nightKey, eventKey)
      : this._eventScheduleDoc(eventKey, subEventId);

    try {
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error("Scaletta non trovata");

      const data = docSnap.data();
      const newScaletta = [...data.scaletta];

      // Aggiorna l'evento specifico mantenendo i campi esistenti
      newScaletta[eventIndex] = {
        ...newScaletta[eventIndex],
        ...updatedEvent,
      };

      await updateDoc(docRef, {
        scaletta: newScaletta,
      });

      return true;
    } catch (error) {
      console.error("Errore nell'aggiornamento dell'evento:", error);
      throw error;
    }
  },

  // Aggiorna l'intera scaletta (es. per riordinamento)
  async updateScaletta(arg1, arg2, arg3, arg4) {
    const legacy = typeof arg1 === "string";
    const subEventId = legacy ? null : arg1;
    const nightKey = legacy ? arg1 : arg2;
    const newScaletta = legacy ? arg2 : arg3;
    const eventKey = legacy ? arg3 || "sanremo" : arg4 || "sanremo";
    const docRef = legacy
      ? this._legacyScheduleDoc(nightKey, eventKey)
      : this._eventScheduleDoc(eventKey, subEventId);
    try {
      await updateDoc(docRef, {
        scaletta: newScaletta,
      });
      return true;
    } catch (error) {
      console.error("Errore nell'aggiornamento della scaletta:", error);
      throw error;
    }
  },

  // Ripristina la scaletta ai dati iniziali (utile per reset)
  async resetSchedule(arg1, arg2, arg3) {
    const legacy = typeof arg1 === "string";
    const subEventId = legacy ? null : arg1;
    const nightKey = legacy ? arg1 : arg2;
    const eventKey = legacy ? arg2 || "sanremo" : arg3 || "sanremo";
    const initialData = initialSchedules[nightKey];
    if (!initialData) return;

    const docRef = legacy
      ? this._legacyScheduleDoc(nightKey, eventKey)
      : this._eventScheduleDoc(eventKey, subEventId);
    await setDoc(docRef, initialData);
  },
};
